import React, { useState, useEffect } from 'react';

/**
 * FirmTypeSelect Component
 * 
 * A dropdown for selecting firm/industry type with an "Other" option
 * that reveals a text input for custom values.
 */
const FirmTypeSelect = ({ 
    id = "firmType",
    name,
    label = "Type of Firm",
    value = "",
    onChange,
    onBlur,
    error,
    required = false,
    disabled = false,
    className = ""
}) => {
    // Get firm types from the form's data attribute
    const getFirmTypes = () => {
        const formElement = document.getElementById('lde-booking-form');
        if (formElement && formElement.dataset.firmTypes) {
            try {
                return JSON.parse(formElement.dataset.firmTypes);
            } catch (e) {
                console.error('Error parsing firm types:', e);
                return [];
            }
        }
        return [];
    };

    const firmTypes = getFirmTypes();
    
    // Determine if current value is "other" (not in the predefined list and not empty)
    const isOtherValue = value && value !== '' && !firmTypes.includes(value);
    
    const [selectValue, setSelectValue] = useState(isOtherValue ? 'other' : (value || ''));
    const [otherValue, setOtherValue] = useState(isOtherValue ? value : '');
    const [showOther, setShowOther] = useState(isOtherValue);
    
    // Track if user explicitly selected "other" to prevent useEffect from resetting
    const [userSelectedOther, setUserSelectedOther] = useState(false);

    // Update internal state when value prop changes
    useEffect(() => {
        // Handle empty value (form reset scenario)
        if (!value || value === '') {
            // Only skip reset if user JUST selected "other" and hasn't typed anything yet
            // (otherValue would also be empty in this case)
            if (userSelectedOther && selectValue === 'other' && otherValue === '') {
                return;
            }
            // Reset to default empty state
            setSelectValue('');
            setOtherValue('');
            setShowOther(false);
            setUserSelectedOther(false);
            return;
        }
        
        // Handle non-empty value
        const isOther = !firmTypes.includes(value);
        if (isOther) {
            setSelectValue('other');
            setOtherValue(value);
            setShowOther(true);
        } else {
            // Value is a valid firm type from the list
            setSelectValue(value);
            setOtherValue('');
            setShowOther(false);
            setUserSelectedOther(false);
        }
    }, [value, firmTypes]);

    const handleSelectChange = (e) => {
        const newValue = e.target.value;
        setSelectValue(newValue);
        
        if (newValue === 'other') {
            setShowOther(true);
            setUserSelectedOther(true);
            // When switching to "other", clear the main value until they type something
            // But keep the other value if it exists
            if (otherValue) {
                onChange({
                    target: {
                        name: name || id,
                        value: otherValue
                    }
                });
            } else {
                onChange({
                    target: {
                        name: name || id,
                        value: ''
                    }
                });
            }
        } else {
            setShowOther(false);
            setOtherValue('');
            setUserSelectedOther(false);
            onChange({
                target: {
                    name: name || id,
                    value: newValue
                }
            });
        }
    };

    const handleOtherChange = (e) => {
        const newValue = e.target.value;
        setOtherValue(newValue);
        onChange({
            target: {
                name: name || id,
                value: newValue
            }
        });
    };

    const handleOtherBlur = (e) => {
        if (onBlur) {
            onBlur({
                target: {
                    name: name || id,
                    value: otherValue
                }
            });
        }
    };

    const handleSelectBlur = (e) => {
        if (onBlur && selectValue !== 'other') {
            onBlur(e);
        }
    };

    // If no firm types are configured, don't render anything
    if (firmTypes.length === 0) {
        return null;
    }

    return (
        <div className={`form-group firm-type-group ${error ? 'has-error' : ''} ${className}`}>
            <label htmlFor={id}>
                {label}
                {required ? (
                    <span className="required-indicator">*</span>
                ) : (
                    <span className="optional-indicator"> (optional)</span>
                )}
            </label>
            
            <select
                id={id}
                name={name || id}
                value={selectValue}
                onChange={handleSelectChange}
                onBlur={handleSelectBlur}
                disabled={disabled}
                className={error ? 'error' : ''}
            >
                <option value="">Choose an option</option>
                {firmTypes.map((type, index) => (
                    <option key={index} value={type}>{type}</option>
                ))}
                <option value="other">Other (please specify)</option>
            </select>
            
            {showOther && (
                <input
                    type="text"
                    id={`${id}_other`}
                    value={otherValue}
                    onChange={handleOtherChange}
                    onBlur={handleOtherBlur}
                    placeholder="Please specify your firm type"
                    className={`firm-type-other ${error ? 'error' : ''}`}
                    disabled={disabled}
                />
            )}
            
            {error && (
                <p className="error-msg">
                    <i className="icon-attention"></i> {error.message}
                </p>
            )}
        </div>
    );
};

export default FirmTypeSelect;
